import { useState } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import * as lucideIcons from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const languageFlags = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦"
};

function IconSearchSelect({ value, onChange }) {
  const [search, setSearch] = useState("");
  const iconNames = Object.keys(lucideIcons).filter((name) => name[0] === name[0].toUpperCase());
  const filtered = iconNames.filter((name) => name.toLowerCase().includes(search.toLowerCase()));
  return (
    <div>
      <Input placeholder="İkon ara..." value={search} onChange={e => setSearch(e.target.value)} />
      <div className="max-h-48 overflow-auto border rounded mt-2 bg-background z-10">
        {filtered.slice(0, 30).map((name) => {
          const Icon = lucideIcons[name];
          return (
            <div key={name} className={`flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-accent ${value === name ? "bg-secondary" : ""}`} onClick={() => onChange(name)}>
              <Icon className="h-5 w-5" />
              <span className="text-foreground">{name}</span>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="px-2 py-1 text-muted-foreground">Sonuç yok</div>}
      </div>
    </div>
  );
}

export default function NewService() {
  const [title, setTitle] = useState({
    tr: "",
    en: "",
    ar: ""
  });
  const [description, setDescription] = useState({
    tr: "",
    en: "",
    ar: ""
  });
  const [icon, setIcon] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const { data: services } = await supabase
      .from("home_services")
      .select("order_num")
      .order("order_num", { ascending: false })
      .limit(1);
    const nextOrder = services && services.length > 0 ? (services[0].order_num || 0) + 1 : 1;
    const { error } = await supabase.from("home_services").insert({
      title: JSON.stringify(title),
      description: JSON.stringify(description),
      icon,
      order_num: nextOrder
    });
    if (error) {
      toast({ title: "Hata", description: "Hizmet eklenemedi.", variant: "destructive" });
    } else {
      toast({ title: "Hizmet eklendi", description: "Yeni hizmet başarıyla eklendi." });
      navigate("/content/home/services");
    }
    setIsSubmitting(false);
  };

  return (
    <ContentLayout title="Yeni Hizmet Ekle" backLink="/content/home/services">
      <form onSubmit={handleSubmit} className="p-6 space-y-6 max-w-xl mx-auto">
        <Tabs defaultValue="tr" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tr">{languageFlags.tr} Türkçe</TabsTrigger>
            <TabsTrigger value="en">{languageFlags.en} English</TabsTrigger>
            <TabsTrigger value="ar">{languageFlags.ar} العربية</TabsTrigger>
          </TabsList>
          <TabsContent value="tr" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title-tr">Başlık (Türkçe)</Label>
              <Input
                id="title-tr"
                placeholder="Hizmet başlığı"
                value={title.tr}
                onChange={(e) => setTitle({ ...title, tr: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description-tr">Açıklama (Türkçe)</Label>
              <Textarea
                id="description-tr"
                placeholder="Hizmet açıklaması"
                value={description.tr}
                onChange={(e) => setDescription({ ...description, tr: e.target.value })}
                rows={5}
              />
            </div>
          </TabsContent>
          <TabsContent value="en" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title-en">Title (English)</Label>
              <Input
                id="title-en"
                placeholder="Service title"
                value={title.en}
                onChange={(e) => setTitle({ ...title, en: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description-en">Description (English)</Label>
              <Textarea
                id="description-en"
                placeholder="Service description"
                value={description.en}
                onChange={(e) => setDescription({ ...description, en: e.target.value })}
                rows={5}
              />
            </div>
          </TabsContent>
          <TabsContent value="ar" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title-ar">العنوان (العربية)</Label>
              <Input
                id="title-ar"
                placeholder="عنوان الخدمة"
                value={title.ar}
                onChange={(e) => setTitle({ ...title, ar: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description-ar">الوصف (العربية)</Label>
              <Textarea
                id="description-ar"
                placeholder="وصف الخدمة"
                value={description.ar}
                onChange={(e) => setDescription({ ...description, ar: e.target.value })}
                rows={5}
              />
            </div>
          </TabsContent>
        </Tabs>
        <div className="space-y-2">
          <Label>İkon</Label>
          <IconSearchSelect value={icon} onChange={setIcon} />
          {icon && <div className="mt-2 flex items-center gap-2 text-muted-foreground"><span>Seçili:</span> {icon && (() => { const Icon = lucideIcons[icon]; return <Icon className="h-5 w-5" />; })()} <span>{icon}</span></div>}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/content/home/services")}>İptal</Button>
          <Button type="submit" disabled={isSubmitting || !icon}>{isSubmitting ? "Kaydediliyor..." : "Kaydet"}</Button>
        </div>
      </form>
    </ContentLayout>
  );
}
